import axios from 'axios';
import {
  BASE_URL,
  GLOBAL_SETTING_COUNTRY_API_PATH,
  GLOBAL_SETTING_DEFAULT_SERVICE_AUTO_DISTRIBUTION_API_PATH,
  GLOBAL_SETTING_LANGUAGE_API_PATH,
  GLOBAL_SETTING_REGION_API_PATH,
} from 'constants/api';
import { FetchResult } from 'type/apiType';
import {
  BulkUpsertResponse,
  CountryInterface,
  DefaultServiceAutoDistributionInterface,
  DefaultServiceAutoDistributionRequest,
  LanguageInterface,
  RegionInterface,
} from '../types/globalSettingType';

class GlobalSettingService {
  // Country APIs
  async fetchCountries(params?: URLSearchParams): Promise<FetchResult<CountryInterface>> {
    const response = await axios.get(`${BASE_URL}${GLOBAL_SETTING_COUNTRY_API_PATH}`, {
      params: params,
    });
    return response.data;
  }

  async fetchCountryById(id: number): Promise<CountryInterface> {
    const response = await axios.get(`${BASE_URL}${GLOBAL_SETTING_COUNTRY_API_PATH}${id}/`);
    return response.data;
  }

  async createCountry(data: Partial<CountryInterface>): Promise<CountryInterface> {
    const response = await axios.post(`${BASE_URL}${GLOBAL_SETTING_COUNTRY_API_PATH}`, data);
    return response.data;
  }

  async updateCountry(id: number, data: Partial<CountryInterface>): Promise<CountryInterface> {
    const response = await axios.patch(`${BASE_URL}${GLOBAL_SETTING_COUNTRY_API_PATH}${id}/`, data);
    return response.data;
  }

  async deleteCountry(id: number): Promise<void> {
    await axios.delete(`${BASE_URL}${GLOBAL_SETTING_COUNTRY_API_PATH}${id}/`);
  }

  // Region APIs
  async fetchRegions(params?: URLSearchParams): Promise<FetchResult<RegionInterface>> {
    const response = await axios.get(`${BASE_URL}${GLOBAL_SETTING_REGION_API_PATH}`, {
      params: params,
    });
    return response.data;
  }

  async fetchRegionById(id: number): Promise<RegionInterface> {
    const response = await axios.get(`${BASE_URL}${GLOBAL_SETTING_REGION_API_PATH}${id}/`);
    return response.data;
  }

  async createRegion(data: Partial<RegionInterface>): Promise<RegionInterface> {
    const response = await axios.post(`${BASE_URL}${GLOBAL_SETTING_REGION_API_PATH}`, data);
    return response.data;
  }

  async updateRegion(id: number, data: Partial<RegionInterface>): Promise<RegionInterface> {
    const response = await axios.patch(`${BASE_URL}${GLOBAL_SETTING_REGION_API_PATH}${id}/`, data);
    return response.data;
  }

  async addCountriesToRegion(regionId: number, countryIds: number[]): Promise<void> {
    await axios.post(`${BASE_URL}${GLOBAL_SETTING_REGION_API_PATH}${regionId}/countries/`, {
      country_ids: countryIds,
    });
  }

  async removeCountriesFromRegion(regionId: number, countryIds: number[]): Promise<void> {
    await axios.delete(`${BASE_URL}${GLOBAL_SETTING_REGION_API_PATH}${regionId}/countries/`, {
      data: { country_ids: countryIds },
    });
  }

  // Language APIs
  async fetchLanguages(params?: URLSearchParams): Promise<FetchResult<LanguageInterface>> {
    const response = await axios.get(`${BASE_URL}${GLOBAL_SETTING_LANGUAGE_API_PATH}`, {
      params: params,
    });
    return response.data;
  }

  async fetchLanguageById(id: number): Promise<LanguageInterface> {
    const response = await axios.get(`${BASE_URL}${GLOBAL_SETTING_LANGUAGE_API_PATH}${id}/`);
    return response.data;
  }

  async createLanguage(data: Partial<LanguageInterface>): Promise<LanguageInterface> {
    const response = await axios.post(`${BASE_URL}${GLOBAL_SETTING_LANGUAGE_API_PATH}`, data);
    return response.data;
  }

  async updateLanguage(id: number, data: Partial<LanguageInterface>): Promise<LanguageInterface> {
    const response = await axios.patch(
      `${BASE_URL}${GLOBAL_SETTING_LANGUAGE_API_PATH}${id}/`,
      data
    );
    return response.data;
  }

  async deleteLanguage(id: number): Promise<void> {
    await axios.delete(`${BASE_URL}${GLOBAL_SETTING_LANGUAGE_API_PATH}${id}/`);
  }

  // Default Service Auto Distribution APIs
  async fetchDefaultServiceAutoDistributions(
    params?: URLSearchParams
  ): Promise<FetchResult<DefaultServiceAutoDistributionInterface>> {
    const response = await axios.get(
      `${BASE_URL}${GLOBAL_SETTING_DEFAULT_SERVICE_AUTO_DISTRIBUTION_API_PATH}`,
      {
        params: params,
      }
    );
    return response.data;
  }

  async bulkUpsertDefaultServiceAutoDistributions(
    data: DefaultServiceAutoDistributionRequest[]
  ): Promise<BulkUpsertResponse> {
    const response = await axios.post(
      `${BASE_URL}${GLOBAL_SETTING_DEFAULT_SERVICE_AUTO_DISTRIBUTION_API_PATH}bulk-upsert/`,
      data
    );
    return response.data;
  }

  async fetchDefaultServiceAutoDistributionById(
    id: number
  ): Promise<DefaultServiceAutoDistributionInterface> {
    const response = await axios.get(
      `${BASE_URL}${GLOBAL_SETTING_DEFAULT_SERVICE_AUTO_DISTRIBUTION_API_PATH}${id}/`
    );
    return response.data;
  }

  async createDefaultServiceAutoDistribution(
    data: DefaultServiceAutoDistributionRequest
  ): Promise<DefaultServiceAutoDistributionInterface> {
    const response = await axios.post(
      `${BASE_URL}${GLOBAL_SETTING_DEFAULT_SERVICE_AUTO_DISTRIBUTION_API_PATH}`,
      data
    );
    return response.data;
  }

  async updateDefaultServiceAutoDistribution(
    id: number,
    data: DefaultServiceAutoDistributionRequest
  ): Promise<DefaultServiceAutoDistributionInterface> {
    const response = await axios.put(
      `${BASE_URL}${GLOBAL_SETTING_DEFAULT_SERVICE_AUTO_DISTRIBUTION_API_PATH}${id}/`,
      data
    );
    return response.data;
  }

  async deleteDefaultServiceAutoDistribution(id: number): Promise<void> {
    await axios.delete(
      `${BASE_URL}${GLOBAL_SETTING_DEFAULT_SERVICE_AUTO_DISTRIBUTION_API_PATH}${id}/`
    );
  }
}

export const globalSettingService = new GlobalSettingService();
